import { useQuery } from "@tanstack/react-query";
import {
  fetchCustomerByAccessId,
  fetchBatchPlannedInvestments,
  fetchBatchMeasures,
} from "@/services/api";
import { useLanguage } from "@/contexts/LanguageContext";
import getProjectTranslation from "@/utils/getProjectTranslation";

// This hook specifically handles the customer dashboard use case
// where we need to get batches from the customer's transactions
export function useCustomerProjectsBatches(accessId: string | undefined) {
  const { language } = useLanguage();

  // First fetch the customer data
  const customerQuery = useQuery({
    queryKey: ["customer", accessId],
    queryFn: async () => {
      if (!accessId) throw new Error("No access ID provided");
      return fetchCustomerByAccessId(accessId);
    },
    enabled: !!accessId,
  });

  // Extract project and batch IDs from customer transactions
  const projects = new Map<
    string,
    { id: string; title: string; batches: { id: string; title: string }[] }
  >();
  // Create a map of project IDs to batch IDs
  const projectToBatchIds: Record<string, string[]> = {};

  if (customerQuery.data?.account?.toTransactions) {
    for (const transaction of customerQuery.data.account.toTransactions) {
      if (transaction.project?.id && transaction.batch?.id) {
        // If project doesn't exist in map yet, add it
        if (!projects.has(transaction.project.id)) {
          projects.set(transaction.project.id, {
            id: transaction.project.id,
            title:
              getProjectTranslation(
                transaction.project.nameTranslations,
                language
              ) || transaction.project.title,
            batches: [],
          });

          // Initialize the batch IDs array for this project
          projectToBatchIds[transaction.project.id] = [];
        }

        // Get the project from the map
        const project = projects.get(transaction.project.id)!;

        // Only add the batch if it doesn't already exist
        if (!project.batches.some((b) => b.id === transaction.batch.id)) {
          project.batches.push({
            id: transaction.batch.id,
            title: transaction.batch.title,
          });

          // Add the batch ID to the project's batch IDs array
          if (
            !projectToBatchIds[transaction.project.id].includes(
              transaction.batch.id
            )
          ) {
            projectToBatchIds[transaction.project.id].push(
              transaction.batch.id
            );
          }
        }
      }
    }
  }

  // Convert map to array for easier use
  const projectsArray = Array.from(projects.values());

  // Fetch batch details for all projects
  const batchDetailsQuery = useQuery({
    queryKey: ["customer-batches-details", accessId],
    queryFn: async () => {
      const allBatchDetails: Record<
        string,
        {
          projectId: string;
          batchId: string;
          plannedInvestments: any[];
          measures: any[];
        }
      > = {};

      // For each project and its batches
      for (const project of projectsArray) {
        for (const batch of project.batches) {
          try {
            // Fetch both planned investments and measures in parallel
            const [plannedInvestments, measures] = await Promise.all([
              fetchBatchPlannedInvestments(batch.id).catch(() => []),
              fetchBatchMeasures(batch.id).catch(() => []),
            ]);

            allBatchDetails[batch.id] = {
              projectId: project.id,
              batchId: batch.id,
              plannedInvestments,
              measures,
            };
          } catch (error) {
            console.error(
              `Error fetching details for batch ${batch.id}:`,
              error
            );
            // Still add an entry with empty arrays
            allBatchDetails[batch.id] = {
              projectId: project.id,
              batchId: batch.id,
              plannedInvestments: [],
              measures: [],
            };
          }
        }
      }

      return allBatchDetails;
    },
    enabled: customerQuery.isSuccess && projectsArray.length > 0,
  });

  return {
    customer: customerQuery.data,
    customerLoading: customerQuery.isLoading,
    customerError: customerQuery.error,
    projects: projectsArray,
    batchDetails: batchDetailsQuery.data || {},
    batchDetailsLoading: batchDetailsQuery.isLoading,
    isLoading: customerQuery.isLoading || batchDetailsQuery.isLoading,
    isError: customerQuery.isError || batchDetailsQuery.isError,
    projectToBatchIds, // Add the map of project IDs to batch IDs
  };
}
